import { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Loader2, Images, Trash2, CheckCircle, XCircle, ImageOff } from "lucide-react";

export const ReferenceImageGallery = ({
  referenceImages,
  loadingRefImages,
  onPreview,
  onDelete,
  headers
}) => {
  if (loadingRefImages) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (referenceImages.length === 0) {
    return (
      <div className="text-center py-12 border-2 border-dashed rounded-lg" data-testid="gallery-empty">
        <Images className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
        <p className="text-muted-foreground">No reference images yet</p>
        <p className="text-sm text-muted-foreground mt-1">Upload images to improve AI accuracy</p>
      </div>
    );
  }

  const positiveCount = referenceImages.filter(i => i.is_positive_example).length;
  const negativeCount = referenceImages.length - positiveCount;

  return (
    <div data-testid="gallery-content">
      <div className="flex items-center gap-3 mb-4">
        <Badge variant="outline" className="text-green-600 border-green-300">
          <CheckCircle className="w-3 h-3 mr-1" /> {positiveCount} positive
        </Badge>
        <Badge variant="outline" className="text-red-600 border-red-300">
          <XCircle className="w-3 h-3 mr-1" /> {negativeCount} negative
        </Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {referenceImages.map((img) => (
          <LazyRefImage key={img.id} img={img} onPreview={onPreview} onDelete={onDelete} headers={headers} />
        ))}
      </div>
    </div>
  );
};

function LazyRefImage({ img, onPreview, onDelete, headers }) {
  const [loaded, setLoaded] = useState(false);
  const [src, setSrc] = useState(
    img.image_url || (img.image_base64 ? `data:image/jpeg;base64,${img.image_base64}` : null)
  );

  const loadFull = async () => {
    if (src || loaded) return;
    setLoaded(true);
    try {
      const axios = (await import("axios")).default;
      const { API } = await import("../../App");
      const res = await axios.get(`${API}/steps/${img.step_id}/reference-images/${img.id}`, { headers });
      if (res.data.image_url) {
        setSrc(res.data.image_url);
      } else if (res.data.image_base64) {
        setSrc(`data:image/jpeg;base64,${res.data.image_base64}`);
      }
    } catch {}
  };

  return (
    <Card className="overflow-hidden group">
      <div className="relative">
        <div
          className="aspect-video bg-muted rounded-md flex items-center justify-center cursor-pointer hover:bg-muted/70 transition-colors overflow-hidden"
          onMouseEnter={loadFull}
          onClick={() => { loadFull(); onPreview(img); }}
        >
          {src ? (
            <img src={src} alt={img.label} className="w-full h-full object-cover" />
          ) : (
            <ImageOff className="w-6 h-6 text-muted-foreground" />
          )}
        </div>
        <Badge
          variant={img.is_positive_example ? "default" : "destructive"}
          className="absolute top-2 right-2 text-xs shadow-sm"
        >
          {img.is_positive_example ? "+" : "−"}
        </Badge>
        <Button
          variant="destructive"
          size="icon"
          className="absolute bottom-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
          onClick={(e) => { e.stopPropagation(); onDelete(img.id); }}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
      <CardContent className="p-2.5">
        <p className="font-medium text-sm truncate">{img.label}</p>
        <p className="text-xs text-muted-foreground truncate">{img.description || "No description"}</p>
      </CardContent>
    </Card>
  );
}
